import axios from "axios";

const API_BASE_URL = "https://aiamo.e-perepletspb.ru/chat-api";
// https://test.testsstand.ru/chat-api
//  "https://aiamo.e-perepletspb.ru/chat-api"
// http://localhost:8080/chat-api

const api = axios.create({
    baseURL: API_BASE_URL,
});

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newAccessToken) {
    refreshSubscribers.forEach((callback) => callback(newAccessToken));
    refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
    refreshSubscribers.push(callback);
}

api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если 401 и токен ещё можно обновить
        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            getRefreshToken()
        ) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve) => {
                    addRefreshSubscriber((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(api(originalRequest));
                    });
                });
            }

            isRefreshing = true;
            try {
                const res = await refreshToken(getRefreshToken());
                const {accessToken, refreshToken: newRefresh} = res.data;

                // сохранить новые токены
                localStorage.setItem("accessToken", accessToken);
                localStorage.setItem("refreshToken", newRefresh);

                api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
                onRefreshed(accessToken);

                // повторяем запрос с новым access-токеном
                return api(originalRequest);
            } catch (err) {
                signOut()
                window.location.reload()
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);


// ================== AppConfig ==================

export async function signUp(username, password) {
    return await axios.post(API_BASE_URL + "/admins/security/sign-up", {
        username: username,
        password: password
    });
}

export async function signIn(username, password) {
    return await axios.post(API_BASE_URL + "/admins/security/sign-in", {
        username: username,
        password: password
    });
}

export async function refreshToken(refreshToken) {
    return await axios.post(API_BASE_URL + "/admins/security/refresh-token", {
        refreshToken: refreshToken
    })
}

export function getAccessToken() {
    return (
        window.localStorage.getItem("accessToken") ||
        window.sessionStorage.getItem("accessToken") ||
        null
    );
}

export function getRefreshToken() {
    return (
        window.localStorage.getItem("refreshToken") ||
        window.sessionStorage.getItem("refreshToken") ||
        null
    );
}

export function signOut() {
    window.localStorage.removeItem("refreshToken")
    window.sessionStorage.removeItem("refreshToken")

    window.localStorage.removeItem("accessToken")
    window.sessionStorage.removeItem("accessToken")
}

/**
 * Получить все конфиги
 */
export async function getAllConfigs() {
    const {data} = await api.get("/admin/config", {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

/**
 * Обновить/создать конфиг по ключу
 * @param {string} key - ключ из enum AppConfigEntity.Key
 * @param {object} params - тело запроса { value, description, type }
 */
export async function upsertConfig(key, params) {
    const {data} = await api.put(`/admin/config/${key}`, params, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

// ================== Prompts ==================

/**
 * Получить все промпты
 */
export async function getAllPrompts() {
    const {data} = await api.get(`/admin/config/prompts`, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

export async function createPrompt(params) {
    const {data} = await api.post(`/admin/config/prompts`, params, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

export async function deletePromptById(id) {
    const {data} = await api.delete(`/admin/config/prompts/${id}`, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

/**
 * Обновить промпт по ID
 * @param {number} id - ID промпта
 * @param {object} params - тело запроса { text, type, maxTokens, temperature, tools }
 */
export async function updatePrompt(id, params) {
    const {data} = await api.put(`/admin/config/prompts/${id}`, params, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

export async function getAllAuditLogs() {
    const {data} = await api.get(`/admin/config/audit-logs`, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`
        }
    });
    return data;
}

export async function getAllAppConfigAuditLogsByRowId(row_id) {
    return getAllByTableNameAndRowId("app_config", row_id)
}

export async function getAllByTableNameAndRowId(table_name, row_id) {
    const {data} = await api.get(`/admin/config/${table_name}/${row_id}/audit-logs`);
    return data;
}

export async function getAllReservations() {
    const {data} = await api.get(`/admin/reservations`);
    return data;
}

export async function getAllMessages(reservationId) {
    const {data} = await api.get(`/admin/reservations/${reservationId}/messages`);
    return data;
}

export async function runReservationAnalysis(reservationId) {
    const {data} = await api.post(`/admin/reservations/${reservationId}/messages/analysis`);
    return data;
}

export async function getReservationAnalysis(reservationId) {
    const {data} = await api.get(`/admin/reservations/${reservationId}`);
    return data;
}

export async function createScript(request) {
    const {data} = await api.post(`/admin/deferred-actions/scripts`, request, {})
    return data;
}

export async function updateScript(id, request) {
    const {data} = await api.put(`/admin/deferred-actions/scripts/${id}`, request, {})
    return data;
}

export async function getScriptById(scriptId) {
    const {data} = await api.get(`/admin/deferred-actions/scripts/${scriptId}`, {})
    return data;
}

export async function getAllScripts() {
    const {data} = await api.get(`/admin/deferred-actions/scripts`, {})
    return data;
}

export async function createDeferredAction(request) {
    const {data} = await api.post(`/admin/deferred-actions`, request, {})
    if (!request.intervalMinutes || request.intervalMinutes <= 0) {
        await deferredActionsRunById(data.id)
    }
}

export async function updateDeferredActionInterval(id, interval) {
    await api.patch(`/admin/deferred-actions/${id}/interval-minutes?interval=${interval}`, null, {})
}

export async function getAllDeferredActions() {
    const {data} = await api.get(`/admin/deferred-actions`)
    return data
}

export async function getDeferredActionById(id) {
    const {data} = await api.get(`/admin/deferred-actions/${id}`)
    return data
}

export async function updateDeferredActionParamValue(id, value) {
    await api.patch(`/admin/deferred-actions/params/${id}?value=${value}`)
}

export async function updateDeferredActionStatus(id, isEnabled) {
    await api.patch(`/admin/deferred-actions/${id}/is-enabled?value=${isEnabled}`);
}

export async function deferredActionsRun() {
    await api.post(`/admin/deferred-actions/run`);
}

export async function deferredActionsRunById(id) {
    await api.post(`/admin/deferred-actions/${id}/run`);
}

export async function deferredActionsRunByIdAndUserId(id, userId) {
    await api.post(`/admin/deferred-actions/${id}/run/by-user-id/${userId}`);
}

export async function getUserAnalyzerResult(userId) {
    const {data} = await api.get(`/admin/users/${userId}/analyzer/result`)
    return data
}

export async function runUserAnalyzer(userId) {
    const {data} = await api.post(`/admin/users/${userId}/analyzer/run`)
    return data
}

export async function runAnalyzerForAllUsers() {
    const {data} = await api.post(`/admin/users/analyzer/run`)
    return data
}

export async function getAllUsers() {
    const {data} = await api.get(`/admin/users`)
    return data
}

/**
 * Получить логи программы лояльности
 */
export async function getLoyaltyProgramLogs({
    page = 0,
    size = 20,
    sortBy = "CREATED_AT",
    direction = "DESC",
    action = null,
    search = null,
    from = null,
    to = null,
} = {}) {
    const params = {
        page,
        size,
        sortBy,
        direction,
    };

    if (action) {
        params.action = action;
    }

    if (search && search.trim() !== "") {
        params.search = search.toString();
    }

    if (from) {
        params.from = from;
    }

    if (to) {
        params.to = to;
    }

    console.log(params);

    const { data } = await api.get(
        "/admin/config/loyalty-program/logs",
        { params }
    );

    return data;
}

export async function getAllLlmTools() {
    const { data } = await api.get(`/admin/config/llm-tools`);
    return data;
}

export async function updateLlmToolDefinition(id, params) {
    const { data } = await api.put(`/admin/config/llm-tools/${id}`, params);
    return data;
}

export async function updateLlmToolParameter(id, params) {
    const { data } = await api.put(`/admin/config/llm-tools/parameters/${id}`, params);
    return data;
}

export async function getAllAiTestContainers() {
    const { data } = await api.get('/admin/ai-test-container');
    return data;
}

export async function createAiTestContainer(payload) {
    const { data } = await api.post('/admin/ai-test-container', payload);
    return data;
}

export async function getAiTestContainerById(id) {
    const { data } = await api.get(`/admin/ai-test-container/${id}`);
    return data;
}

export async function addChatsToAiTestContainer(id, chats) {
    const { data } = await api.post(`/admin/ai-test-container/${id}/add-chats`, chats);
    return data;
}

export async function countEvalResults(payload) {
    const {data} = await api.post(`/admin/deferred-actions/count-eval-results`, payload);
    return data;
}

export async function getAllOurEvents() {
    const { data } = await api.get('/admin/our-event');
    return data;
}
export const createOurEvent = async (eventData) => {
    const response = await api.post('/admin/our-event', eventData);
    return response.data;
};


export const deleteOurEvent = async (eventId) => {
    const response = await api.delete(`/admin/our-event/${eventId}`);
    return response.data;
};

export const updateOurEvent = async (eventId, eventData) => {
    const response = await api.put(`/admin/our-event/${eventId}`, eventData);
    return response.data;
};

// ================== Vector Storage ==================

export async function getAllVectorPoints(offset = 0, limit = 100) {
    const { data } = await api.get(`/admin/vector-storage/points`, {
        params: { offset, limit }
    });
    return data;
}

export async function addVectorPoint(payload) {
    const { data } = await api.post(`/admin/vector-storage/point`, payload);
    return data;
}

export async function deleteVectorPoints(ids) {
    const { data } = await api.delete(`/admin/vector-storage/points`, {
        data: { ids }
    });
    return data;
}

export async function upsertVectorPoint(payload) {
    const { data } = await api.put(`/admin/vector-storage/point`, payload);
    return data;
}
